// Células de saúde da LISTA de pagamentos. Só apresentam: o veredito, as
// regras e as contagens vêm prontos do backend (orderHealth.js) — o frontend
// não recalcula nem reinterpreta nenhum critério.
import { useState } from 'react';
import { ArrowClockwise, Check, Clock, CurrencyDollar, Warning, WarningCircle } from 'shared/icons';
import api from '../../services/api';
import { GetCampaignTypeLabel } from '../../constants/campaignTypes';
import { HEALTH_DIMENSION_LABELS, HEALTH_STATE_BADGE } from '../../constants/paymentHealth';
import { RebateStyle } from '../../constants/paymentRebate';
import { FormatMoney } from '../../utils/format';

// Cores para semáforo por dimensão
const DIMENSION_STYLES = {
    critical: 'bg-red-500',
    warn: 'bg-yellow-500',
    pending: 'bg-gray-400',
    info: 'bg-blue-500',
    ok: 'bg-green-500',
    unchecked: 'bg-gray-300 dark:bg-gray-600',
};

const DIMENSION_ORDER = ['COMERCIAL', 'PAGAMENTO', 'LIQUIDACAO', 'FISCAL', 'LOGISTICA'];

/**
 * Semáforo de saúde: 5 ícones em cores que representam as dimensões
 * (Venda, Pagamento, Recebimento, Fiscal, Logística). O tooltip traz a análise.
 */
export function HealthCell({ health }) {
    if (!health) {
        return <span className="text-mono-400">—</span>;
    }
    const { reasons = [], dimensions = {} } = health;

    const dimensoes = Object.entries(dimensions)
        .filter(([, state]) => state !== 'unchecked')
        .map(([dim, state]) => `${HEALTH_DIMENSION_LABELS[dim] || dim}: ${HEALTH_STATE_BADGE[state]?.label || state}`);
    const naoChecadas = Object.entries(dimensions)
        .filter(([, state]) => state === 'unchecked')
        .map(([dim]) => HEALTH_DIMENSION_LABELS[dim] || dim);
    const title = [
        reasons.length
            ? reasons.map((r) => `${r.id} — ${r.label}`).join('\n')
            : 'Nenhum apontamento nas regras da lista',
        '',
        ...dimensoes,
        naoChecadas.length ? `Não checado nesta lista: ${naoChecadas.join(', ')}` : null,
        '',
        'Abra o pedido para a análise completa.',
    ]
        .filter((line) => line !== null)
        .join('\n');

    return (
        <div className="flex items-center gap-1.5" title={title}>
            {DIMENSION_ORDER.map((dim) => {
                // Dimensão ausente vira `unchecked`, NUNCA `ok`. Pintar de verde
                // o que não foi avaliado é a pior mentira que este semáforo pode
                // contar: a base tem cobertura parcial (extrato e acervo de NF-e
                // não cobrem todo o período), e foi justamente para não afirmar
                // "está tudo certo" sobre dado ausente que o veredito tem três
                // estados em vez de dois.
                const state = dimensions[dim] || 'unchecked';
                const bgClass = DIMENSION_STYLES[state] || DIMENSION_STYLES.ok;
                return (
                    <div
                        key={dim}
                        className={`h-6 w-6 rounded-full ${bgClass}`}
                        title={`${HEALTH_DIMENSION_LABELS[dim] || dim}: ${HEALTH_STATE_BADGE[state]?.label || state}`}
                    />
                );
            })}
        </div>
    );
}

/**
 * Tem movimentação no extrato? Cifrão aceso = o dinheiro desta order já
 * apareceu no Released Money report importado; apagado = ainda não (o que
 * costuma ser cobertura do extrato, não problema).
 * Sem contagem — apenas ícone verde ou cinza.
 */
export function MovementsIndicator({ hasMovements, result = null }) {
    const title = hasMovements
        ? `Lançamento(s) do extrato MP nesta order` +
          (result != null ? ` · resultado R$ ${(Number(result) || 0).toFixed(2)}` : '')
        : 'Sem lançamentos do extrato MP para esta order';
    return (
        <span className="inline-flex items-center gap-1" title={title} role="img" aria-label={title}>
            <CurrencyDollar
                size={18}
                weight={hasMovements ? 'fill' : 'regular'}
                className={hasMovements ? 'text-teal-600 dark:text-teal-400' : 'text-mono-300 dark:text-mono-600'}
            />
        </span>
    );
}

const REBATE_ICONS = { check: Check, clock: Clock, warning: Warning };

// Os números do `rebate` chegam do wire como number OU string decimal do
// MySQL ("5.00") — e null quando o dado não existe. Toda leitura passa por
// aqui para não confundir "zero" (valor legítimo) com "não sei" (null).
const isNum = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

// "3,6" de 3.6 e "5" de "5.00": percentual de campanha sem casa fantasma.
// FormatPercent arredonda para inteiro e comeria o 3,6% da SMART.
const pct = (v) => String(Number(v)).replace('.', ',');

// Diferença sempre com sinal explícito — "R$ -12,00" do Intl lê mal.
const signedMoney = (v) => (v < 0 ? `−${FormatMoney(-v)}` : `+${FormatMoney(v)}`);

const SETTLES_VIA_PT = { coupon: 'cupom', sale_fee: 'tarifa' };
const BASE_KIND_PT = {
    gross: 'preço pago',
    paid_net_of_seller_coupon: 'valor pago, sem o cupom do vendedor',
    pix_legs_net_of_seller_coupon: 'soma das pernas Pix do pedido, sem o cupom do vendedor',
    original_price: 'preço original',
    campaign_price: 'preço de campanha',
};
// Nome do mecanismo pelo qual o rebate foi conferido — sempre à mostra, porque
// "faltou via cupom" e "faltou via tarifa" são investigações diferentes.
const MECANISMO_PT = { coupon: 'cupom do ML no pagamento', sale_fee: 'desconto na tarifa (extrato)' };
const MECANISMO_CURTO = { coupon: 'Cupom', sale_fee: 'Tarifa' };
// O MESMO mecanismo dito como ação, para o chip do card: o que o ML fez para
// pagar o rebate. A forma longa acima é do tooltip da lista, onde cabe a fonte.
const MECANISMO_CHIP = { coupon: 'cupom do ML', sale_fee: 'desconto na tarifa' };
const COBERTURA_PT = {
    sem_anuncio: 'pedido ainda não sincronizado ou sem anúncio ligado',
    antes_do_historico: 'venda anterior ao histórico de campanhas',
    sem_data: 'sem data de referência',
};
const dataCurta = (v) => (v ? new Date(v).toLocaleDateString('pt-BR') : null);

/**
 * Decompõe o `rebate` (contrato: received/promised/difference/tolerance) no que
 * a tela mostra: três totais e duas listas curtas. Um único lugar interpreta os
 * campos, para o tooltip da lista e o painel da análise dizerem a mesma coisa.
 * Tudo já vem calculado do backend; aqui só se seleciona e formata.
 */
// Por que a venda foi desfeita, na língua do extrato → na do usuário.
const MOTIVO_CANCELAMENTO = {
    refund: 'estorno integral',
    mediation: 'devolução ou reclamação decidida pro comprador',
    order_cancelled: 'pedido cancelado no ML',
    payment_rejected: 'pagamento recusado',
    payment_cancelled: 'pagamento cancelado',
    payment_refunded: 'pagamento devolvido',
    payment_charged_back: 'chargeback',
};

export function RebateBreakdown(rebate) {
    const received = rebate?.received || {};
    const promised = rebate?.promised || {};
    const campaigns = Array.isArray(promised.campaigns) ? promised.campaigns : [];
    const num = (v) => (isNum(v) ? Number(v) : null);

    // Só o que entrou de fato: zero não é notícia — "cupom R$ 0,00 · desconto na
    // tarifa R$ 12,05" era o que deixava a linha densa sem dizer mais nada.
    const recebidoItens = [
        { label: 'Cupom do ML', value: num(received.coupon_ml) },
        { label: 'Desconto na tarifa', value: num(received.fee_discount) },
    ].filter((item) => item.value !== null && item.value > 0);

    const cupomNosso = num(received.coupon_seller);
    return {
        prometido: num(promised.total),
        recebido: num(received.total),
        diferenca: num(rebate?.difference),
        tolerancia: num(rebate?.tolerance),
        recebidoItens,
        // Sem extrato o desconto na tarifa ainda é desconhecido: o total só tem o cupom.
        tarifaPendente: received.source === 'payment',
        // Tarifa veio da consulta ao ML (botão "Buscar rebate no ML"), antes do
        // extrato liberar; com extrato, a consulta fica como tira-teima.
        tarifaPelaConsulta: received.source === 'ml_discounts',
        tiraTeimaMl:
            received.source === 'extrato' && num(received.ml_fee_discount) !== null
                ? num(received.ml_fee_discount)
                : null,
        cupomNosso: cupomNosso !== null && cupomNosso > 0 ? cupomNosso : null,
        // "A comissão era X e ficou Y": onde o rebate de tarifa realmente
        // aparece. Campanha `sale_fee` não credita dinheiro — o ML cobra menos.
        feeRelief: rebate?.fee_relief
            ? {
                  antes: num(rebate.fee_relief.commission_before),
                  depois: num(rebate.fee_relief.commission_after),
                  valor: num(rebate.fee_relief.rebate),
                  fonte: rebate.fee_relief.source || null,
              }
            : null,
        // Prometido veio do número que o ML declarou para o pedido (exato) e
        // não da conta pelo percentual arredondado da campanha.
        prometidoDeclarado: campaigns.some((c) => c.expected_source === 'ml_declared'),
        campanhas: campaigns.map((c) => ({
            id: c.campaign_id,
            nome: c.campaign_name || null,
            tipo: c.campaign_type || null,
            // Rótulo oficial em português (constants/campaignTypes) — "PRICE_MATCHING"
            // é identificador de API, não nome de campanha para quem lê o laudo.
            tipoLabel: GetCampaignTypeLabel(c.campaign_type, null),
            pct: isNum(c.meli_percentage) ? pct(c.meli_percentage) : null,
            base: num(c.base_amount),
            baseKind: BASE_KIND_PT[c.base_kind] || null,
            expected: num(c.expected),
            via: SETTLES_VIA_PT[c.settles_via] || null,
        })),
        anuncio: rebate?.item_id || null,
        anuncioTitulo: rebate?.item_title || null,
        // Order paga em várias pernas: o cupom foi conferido no pedido inteiro.
        cupomNoPedido: received.coupon_scope === 'order' ? Number(received.legs) || null : null,
        cancelado: Boolean(rebate?.cancelled),
        motivoCancelamento: MOTIVO_CANCELAMENTO[rebate?.cancelled_reason] ?? 'venda desfeita',
        disputa: Boolean(rebate?.disputed),
        mecanismo: (Array.isArray(rebate?.checked_via) ? rebate.checked_via : [])
            .map((via) => MECANISMO_PT[via])
            .filter(Boolean),
        mecanismoChips: (Array.isArray(rebate?.checked_via) ? rebate.checked_via : [])
            .map((via) => MECANISMO_CHIP[via])
            .filter(Boolean),
        // Veredito POR mecanismo: é o que responde "faltou onde?" sem somar
        // cupom com tarifa. Só as linhas com algo a dizer (prometido ou recebido).
        porMecanismo: Object.entries(rebate?.mechanisms || {})
            .filter(
                ([, m]) =>
                    (isNum(m?.promised) && Number(m.promised) > 0) ||
                    (isNum(m?.received) && Number(m.received) > 0) ||
                    m?.state === 'pendente',
            )
            .map(([via, m]) => ({
                via,
                nome: MECANISMO_CURTO[via] || via,
                prometido: num(m.promised),
                recebido: num(m.received),
                diferenca: num(m.difference),
                // Cupom acima do prometido: a campanha foi paga e o resto é
                // bônus de programa do ML — mostrado à parte, não como erro.
                extra: num(m.extra) > 0 ? num(m.extra) : null,
                estado: m.state,
                label: RebateStyle(m.state).label,
            })),
        motivoCobertura: COBERTURA_PT[rebate?.coverage_reason] || null,
        liberacaoPrevista: dataCurta(rebate?.release_expected_at),
    };
}

const moneyOrDash = (v) => (v === null ? '—' : FormatMoney(v));

// Tooltip da lista: veredito, os três números numa linha e o detalhe em duas
// linhas curtas. Ler tudo é papel do painel da análise, não do title.
function rebateTitle(rebate) {
    const style = RebateStyle(rebate.state);
    const b = RebateBreakdown(rebate);
    const linhas = [b.mecanismo.length ? `${style.label} · via ${b.mecanismo.join(' + ')}` : style.label];
    if (b.cancelado) {
        linhas.push(`Venda desfeita (${b.motivoCancelamento}): tarifa e aporte do ML revertidos.`);
    }
    if (b.disputa) {
        linhas.push('Reclamação/mediação aberta: veredito suspenso.');
    }
    if (b.motivoCobertura) {
        linhas.push(`Sem conferência: ${b.motivoCobertura}.`);
    }
    if (rebate.state === 'pendente' && b.liberacaoPrevista) {
        linhas.push(`Liberação prevista em ${b.liberacaoPrevista}.`);
    }
    if (b.cupomNoPedido) {
        linhas.push(`Cupom conferido no pedido inteiro (${b.cupomNoPedido} pagamentos).`);
    }
    for (const m of b.porMecanismo) {
        const bonus = m.extra ? ` (+ ${FormatMoney(m.extra)} de bônus do ML)` : '';
        linhas.push(
            `${m.nome}: recebido ${moneyOrDash(m.recebido)} · previsto ${moneyOrDash(m.prometido)} → ${m.label}${bonus}`,
        );
    }
    if (b.prometido !== null || b.recebido !== null) {
        linhas.push(
            [
                `Prometido ${moneyOrDash(b.prometido)}`,
                `Recebido ${moneyOrDash(b.recebido)}`,
                b.diferenca !== null ? `Diferença ${signedMoney(b.diferenca)}` : null,
            ]
                .filter(Boolean)
                .join(' · '),
        );
    }
    if (b.recebidoItens.length) {
        linhas.push(
            `ML deu: ${b.recebidoItens.map((i) => `${i.label.toLowerCase()} ${FormatMoney(i.value)}`).join(' · ')}`,
        );
    }
    if (b.tarifaPelaConsulta) {
        linhas.push('Desconto na tarifa pela consulta ao ML (extrato ainda não liberado).');
    }
    if (b.tiraTeimaMl !== null) {
        linhas.push(`Consulta ao ML disse ${FormatMoney(b.tiraTeimaMl)} de desconto na tarifa.`);
    }
    if (!b.recebidoItens.length && b.tarifaPendente) {
        linhas.push('ML deu: desconto na tarifa ainda não liberado');
    }
    if (b.cupomNosso !== null) {
        linhas.push(`Cupom nosso (não é rebate): ${FormatMoney(b.cupomNosso)}`);
    }
    if (b.campanhas.length) {
        const previam = b.campanhas.map((c) =>
            [c.nome, c.id, c.tipo, c.pct ? `${c.pct}%` : null, '→', moneyOrDash(c.expected)].filter(Boolean).join(' '),
        );
        linhas.push(`Previam: ${previam.join('; ')}`);
    }
    if (b.anuncio) {
        linhas.push(`Anúncio ${b.anuncio}${b.anuncioTitulo ? ` — ${b.anuncioTitulo}` : ''}`);
    }
    return linhas.join('\n');
}

// Texto curto da diferença, só nos estados em que ela é a notícia.
function rebateDeltaText(rebate) {
    if (!isNum(rebate.difference)) {
        return null;
    }
    const diff = Number(rebate.difference);
    if (rebate.state === 'faltou') {
        return `faltou ${FormatMoney(Math.abs(diff))}`;
    }
    if (rebate.state === 'a_mais') {
        return `+ ${FormatMoney(diff)}`;
    }
    return null;
}

export function RebateIndicator({ rebate }) {
    if (!rebate) {
        return <span className="text-mono-400">—</span>;
    }
    const style = RebateStyle(rebate.state);
    const Icon = style.icon ? REBATE_ICONS[style.icon] : null;
    const valor = isNum(rebate.received?.total) ? FormatMoney(rebate.received.total) : '—';
    const delta = rebateDeltaText(rebate);
    const ariaLabel = [style.label, valor, delta].filter(Boolean).join(' · ');
    return (
        <span
            className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs tabular-nums"
            title={rebateTitle(rebate)}
            role="img"
            aria-label={ariaLabel}
        >
            <span
                className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${style.dot}`}
            >
                {Icon ? <Icon size={14} weight="bold" /> : style.glyph}
            </span>
            <span className="text-mono-700 dark:text-mono-200">{valor}</span>
            {delta ? <span className={`font-medium ${style.text}`}>{delta}</span> : null}
        </span>
    );
}

/** Aviso de cobertura: até onde o veredito consegue enxergar. */
export function CoverageNote({ coverage }) {
    if (!coverage) {
        return null;
    }
    const dia = (v) => (v ? new Date(v).toLocaleDateString('pt-BR') : '—');
    return (
        <p className="flex items-start gap-1.5 text-xs text-mono-500">
            <WarningCircle size={14} className="mt-0.5 shrink-0" />
            <span>
                O veredito só conclui até onde há dado importado: extrato MP de {dia(coverage.extrato_min)} a{' '}
                {dia(coverage.extrato_max)}
                {coverage.fiscal_available
                    ? ` · acervo de NF-e até ${dia(coverage.acervo_nfe_max)}`
                    : ' · acervo de NF-e indisponível'}
                . Fora disso a linha fica <strong>Aguardando</strong> — nunca é marcada como OK por falta de dado.
            </span>
        </p>
    );
}

/**
 * Botão "Buscar rebate no ML": consulta agora o desconto de tarifa do pedido
 * no Mercado Livre e os pagamentos no Mercado Pago, e devolve o veredito
 * recalculado — fecha o "pendente" da tarifa antes de o extrato liberar. O
 * prometido continua vindo do histórico de campanhas: sem histórico na data,
 * o pedido segue "sem cobertura" mesmo depois da busca.
 */
export function RebateRefreshButton({ orderId, onRefreshed, compact = false }) {
    const [busy, setBusy] = useState(false);
    const [erro, setErro] = useState(null);
    const buscar = async (event) => {
        event?.stopPropagation?.();
        if (busy || !orderId) {
            return;
        }
        setBusy(true);
        setErro(null);
        try {
            const res = await api.refreshOrderRebate(orderId);
            onRefreshed?.(res);
        } catch (e) {
            setErro(e?.message || 'Não foi possível consultar o Mercado Livre agora.');
        } finally {
            setBusy(false);
        }
    };
    const titulo = erro
        ? erro
        : 'Buscar rebate no ML agora: consulta o desconto de tarifa do pedido e os pagamentos no MP';
    if (compact) {
        return (
            <button
                type="button"
                onClick={buscar}
                disabled={busy}
                title={titulo}
                aria-label="Buscar rebate no ML"
                className={`inline-flex h-6 w-6 items-center justify-center rounded-full border text-mono-500 transition-colors hover:border-mono-500 hover:text-black disabled:cursor-wait disabled:opacity-60 dark:hover:text-white ${
                    erro
                        ? 'border-danger-300 text-danger-600 dark:border-danger-800 dark:text-danger-400'
                        : 'border-mono-200 dark:border-mono-700'
                }`}
            >
                <ArrowClockwise size={12} className={busy ? 'animate-spin' : ''} />
            </button>
        );
    }
    return (
        <button
            type="button"
            onClick={buscar}
            disabled={busy}
            title={titulo}
            className="inline-flex items-center gap-1.5 rounded-lg border border-mono-300 bg-white px-3 py-1.5 text-sm font-medium text-mono-800 transition-colors hover:border-mono-500 hover:text-black disabled:cursor-wait disabled:opacity-60 dark:border-mono-700 dark:bg-mono-950 dark:text-mono-200 dark:hover:border-mono-500 dark:hover:text-white"
        >
            <ArrowClockwise size={16} className={busy ? 'animate-spin' : ''} />
            {busy ? 'Consultando o ML…' : 'Buscar rebate no ML'}
        </button>
    );
}
